import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play, ArrowUpRight } from "lucide-react";
import PageShell from "@/components/PageShell";
import PageHeader from "@/components/PageHeader";
import TraditionTag from "@/components/TraditionTag";
import { films, channels } from "@/data/enrichments";
import { ytEmbedUrl, ytWatchUrl, ytThumbUrl } from "@/lib/youtube";

const YT_CHANNELS = [
  {
    id: "youtube",
    label: "DharmaWeave",
    handle: "@dharmaweave",
    description:
      "Daily shorts, reels, and music videos — the front door of the practice.",
    url: "https://youtube.com/@dharmaweave",
  },
  {
    id: "youtube-epics",
    label: "DharmaWeave · Epics",
    handle: "@dharmaweaveepics",
    description:
      "Long-form sacred films, 10–60 minutes. Meditation-paced. Painterly.",
    url: "https://youtube.com/@dharmaweaveepics",
  },
];

function FilmCard({ film, index }) {
  const [playing, setPlaying] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{
        duration: 0.85,
        delay: (index % 2) * 0.07,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group"
    >
      <div className="card-shrine rounded-sm overflow-hidden aspect-video bg-shrine-shadow border-cream/15 relative">
        {playing ? (
          <iframe
            src={ytEmbedUrl(film.youtube_video_id, { autoplay: 1 })}
            title={film.title}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        ) : (
          <button
            onClick={() => setPlaying(true)}
            className="absolute inset-0 w-full h-full"
            aria-label={`Play ${film.title}`}
          >
            <img
              src={ytThumbUrl(film.youtube_video_id, "maxresdefault")}
              alt={film.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src = ytThumbUrl(film.youtube_video_id, "hqdefault");
              }}
              loading="lazy"
            />
            <div className="absolute inset-0 bg-shrine-shadow/35 group-hover:bg-shrine-shadow/15 transition-colors duration-700 ease-breath flex items-center justify-center">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-cream/95 flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform duration-700 ease-breath">
                <Play size={20} className="text-ink ml-0.5" fill="currentColor" />
              </div>
            </div>
          </button>
        )}
      </div>
      <div className="mt-4">
        <p className="font-body text-xs text-ink-mute tracking-widest2 uppercase">
          {film.subtitle}
        </p>
        <h3 className="font-display text-xl md:text-2xl text-ink leading-tight tracking-tightish mt-2">
          {film.title}
        </h3>
        <p className="font-serif text-ink-soft text-base md:text-lg leading-relaxed mt-2.5 max-w-prose">
          {film.description}
        </p>
        <a
          href={ytWatchUrl(film.youtube_video_id)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-3 font-body text-xs tracking-widest2 uppercase text-dharma-gold-deep hover:text-saffron-fire transition-colors"
        >
          Watch on YouTube <ArrowUpRight size={12} />
        </a>
      </div>
    </motion.div>
  );
}

function FilmsGrid() {
  if (!films || films.length === 0) return null;
  return (
    <section className="py-12 md:py-16">
      <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-14">
          {films.map((f, i) => (
            <FilmCard key={f.id} film={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ChannelsGrid() {
  return (
    <section className="bg-cream py-16 md:py-24">
      <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14">
        <p className="eyebrow mb-4">Channels</p>
        <h2 className="font-display text-3xl md:text-5xl text-ink tracking-tightish max-w-3xl">
          Two rooms, one practice.
        </h2>
        <p className="font-serif text-ink-soft text-lg md:text-xl mt-5 max-w-2xl leading-relaxed">
          Each channel is built for a different attention span. Scroll on the
          main feed; settle in for a film on Epics.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 max-w-5xl">
          {YT_CHANNELS.map((c, i) => (
            <motion.a
              key={c.id}
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.85,
                delay: i * 0.07,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="card-shrine rounded-sm p-7 md:p-8 block group"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display text-xl md:text-2xl text-ink leading-tight">
                    {c.label}
                  </div>
                  <div className="font-body text-xs text-ink-mute mt-1.5 tracking-widest2 uppercase">
                    {c.handle}
                  </div>
                </div>
                <ArrowUpRight
                  size={20}
                  className="text-ink-mute group-hover:text-dharma-gold-deep transition-colors duration-700 ease-breath"
                />
              </div>
              <p className="font-serif text-ink-soft text-base md:text-lg leading-relaxed mt-5">
                {c.description}
              </p>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Watch() {
  return (
    <PageShell title="Watch">
      <PageHeader
        eyebrow="Films"
        title="Watch the scripture come alive."
        lede="Long-form sacred films and a daily current of shorts. Painterly. Patient. Always anchored to a verse."
      />
      <FilmsGrid />
      <ChannelsGrid />
    </PageShell>
  );
}
